import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import aiCostsFiltersSignal, {
	setAiCostsFilters,
} from '@UI/Pages/Administration/AiCosts/Filters/Signals/aiCostsFiltersSignal';
import { css } from '@emotion/react';
import { ChartNoAxesColumn } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { Button, Skeleton } from '@verclam/ui-kit';

const SKELETON_ROW_KEYS = ['first', 'second', 'third', 'fourth', 'fifth'] as const;

const skeletonStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

type Props<TList extends { readonly list: readonly unknown[] }> = {
	/** `null` until the first page has arrived. */
	readonly list: TList | null;
	readonly error: unknown;
	readonly onRetry: () => void;
	readonly children: (list: TList) => React.ReactNode;
};

/** The loading, error and empty states shared by every tab; the list itself is drawn by `children`. */
const CostListState = <TList extends { readonly list: readonly unknown[] }>({
	list,
	error,
	onRetry,
	children,
}: Props<TList>): React.ReactNode => {
	const filters = useSignalValue(aiCostsFiltersSignal);

	const handleClearScope = React.useCallback(() => {
		setAiCostsFilters(aiCostsFiltersSignal.peek().clearScope());
	}, []);

	const handleResetPeriod = React.useCallback(() => {
		setAiCostsFilters(aiCostsFiltersSignal.peek().resetToCurrentMonth());
	}, []);

	if (error !== null && error !== undefined) {
		return (
			<ErrorState
				title="Les coûts n'ont pas pu être chargés"
				description="Vos filtres sont conservés."
				onRetry={onRetry}
			/>
		);
	}

	if (list === null) {
		return (
			<div css={skeletonStyle} aria-busy="true">
				{SKELETON_ROW_KEYS.map((rowKey) => (
					<Skeleton
						key={rowKey}
						height="2.75rem"
						borderRadius={theme.radii.xsmall}
						animation="wave"
					/>
				))}
			</div>
		);
	}

	if (list.list.length === 0) {
		return (
			<EmptyState
				title="Aucun audio ni document sur cette période"
				description={
					filters.hasScope
						? "Rien n'a été traité pour ces filtres sur cette période."
						: "Rien n'a été traité sur cette période."
				}
				icon={<ChartNoAxesColumn size={28} />}
				action={
					filters.hasScope ? (
						<Button type="button" link label="Effacer les filtres" onClick={handleClearScope} />
					) : (
						<Button
							type="button"
							link
							label="Revenir au mois en cours"
							onClick={handleResetPeriod}
						/>
					)
				}
			/>
		);
	}

	return <React.Fragment>{children(list)}</React.Fragment>;
};

export default CostListState;
